import type { ReviewOption } from '@iheartjane/menu';
import { FEELING_ICONS } from '@iheartjane/menu';
import type { Nullish } from '@iheartjane/types';
import { getCookieFromDocument, setCookie } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFetcher } from 'react-router';

import type { ActionResult } from '@jane/bloom/actions/remixAction';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { durations } from '@jane/bloom/utils/durations';
import { PROMPTS, getPromptCookieName } from '@jane/bloom/utils/prompts';
import type {
  ReviewTagOption,
  ReviewTagOptions,
} from '@jane/bloom/viewModels/reviewTagOptions';

import { LeaveReviewModal } from './leaveReviewModal';
import type { PromptDismissal } from './promptDismissal';
import {
  nextDismissal,
  parseDismissal,
  serializeDismissal,
  shouldShowPrompt,
} from './promptDismissal';
import type { LeaveReviewProduct, OpenLeaveReviewArgs } from './types';

interface LeaveReviewContextValue {
  /** Hides the prompt until the snooze ends or a new product to review appears. */
  dismissPrompt: () => void;
  /** Whether Leave Review is available to this user; entry points render nothing otherwise. */
  isEnabled: boolean;
  /** An open was requested and the modal is waiting on review question options; false once they fail to load. */
  isOpening: boolean;
  /** Whether the modal can open: entry points wait for this instead of opening half-loaded. */
  isReady: boolean;
  openLeaveReview: (args: OpenLeaveReviewArgs) => void;
  /** Most recent purchase first; empty while the prompt is hidden. */
  promptProducts: LeaveReviewProduct[];
  refreshPrompt: () => void;
  /** Hides the prompt like `dismissPrompt`, without counting as a dismissal. */
  snoozePrompt: () => void;
}

const LeaveReviewContext = createContext<LeaveReviewContextValue>({
  dismissPrompt: () => undefined,
  isEnabled: false,
  isOpening: false,
  isReady: false,
  openLeaveReview: () => undefined,
  promptProducts: [],
  refreshPrompt: () => undefined,
  snoozePrompt: () => undefined,
});

export const useLeaveReview = () => useContext(LeaveReviewContext);

const toFeelingOption = ({ id, label }: ReviewTagOption): ReviewOption => {
  const Icon = FEELING_ICONS[label];
  return {
    icon: Icon ? <Icon color={null} size="md" /> : undefined,
    id,
    label,
  };
};

const readDismissal = (cookieName: string) =>
  parseDismissal(getCookieFromDocument(cookieName), Date.now());

interface LeaveReviewProviderProps {
  children: ReactNode;
  cookiePrefix: Nullish<string>;
  /** Leave Review flag is on and the user is logged in. */
  enabled: boolean;
  /** The current page opens the modal itself, so review question options load up front. */
  preloadOptions?: boolean;
  /** The current page shows the prompt (PLP and PDP). */
  showPrompt: boolean;
}

/**
 * Makes the Leave Review modal openable from anywhere below it and decides
 * when the prompt shows. Review question options load with the prompt, so the
 * modal never opens without them.
 */
export const LeaveReviewProvider = ({
  children,
  cookiePrefix,
  enabled,
  preloadOptions = false,
  showPrompt,
}: LeaveReviewProviderProps) => {
  const tagsFetcher = useFetcher<ActionResult<ReviewTagOptions>>();
  const productsFetcher = useFetcher<ActionResult<LeaveReviewProduct[]>>();
  const hasRequestedTagsRef = useRef(false);
  const requestedProductsVersionRef = useRef<number | null>(null);
  const [productsVersion, setProductsVersion] = useState(0);
  const cookieName = getPromptCookieName(PROMPTS.leaveReview, cookiePrefix);
  // Null on the server; safe on hydration since nothing renders until products
  // load client-side.
  const [dismissal, setDismissal] = useState<PromptDismissal | null>(() =>
    typeof document === 'undefined' ? null : readDismissal(cookieName)
  );
  const [openArgs, setOpenArgs] = useState<OpenLeaveReviewArgs | null>(null);

  useEffect(() => {
    if (!enabled || !showPrompt) return;
    if (requestedProductsVersionRef.current === productsVersion) return;
    requestedProductsVersionRef.current = productsVersion;
    productsFetcher.load('/api/unreviewed-products');
  }, [enabled, showPrompt, productsFetcher, productsVersion]);

  const products = useMemo(
    () => (productsFetcher.data?.success ? productsFetcher.data.payload : []),
    [productsFetcher.data]
  );
  const isPromptDue =
    dismissal !== null &&
    shouldShowPrompt(
      dismissal,
      products.map(({ id }) => id)
    );

  useEffect(() => {
    const needsOptions = isPromptDue || openArgs !== null || preloadOptions;
    if (!enabled || !needsOptions || hasRequestedTagsRef.current) return;
    hasRequestedTagsRef.current = true;
    tagsFetcher.load('/api/product-reviews');
  }, [enabled, isPromptDue, openArgs, preloadOptions, tagsFetcher]);

  const tagOptions = tagsFetcher.data?.success
    ? tagsFetcher.data.payload
    : null;
  const options = useMemo(
    () =>
      tagOptions && {
        activityOptions: tagOptions.activities,
        feelingOptions: tagOptions.feelings.map(toFeelingOption),
      },
    [tagOptions]
  );
  const isReady = enabled && options !== null;
  const hasTagsFailed =
    tagsFetcher.state === 'idle' && tagsFetcher.data?.success === false;
  const isOpening = openArgs !== null && !isReady && !hasTagsFailed;

  const promptProducts = useMemo(
    () => (isReady && showPrompt && isPromptDue && !openArgs ? products : []),
    [isPromptDue, isReady, openArgs, products, showPrompt]
  );

  const openLeaveReview = useCallback(
    (args: OpenLeaveReviewArgs) => {
      if (!enabled) return;
      if (hasTagsFailed) tagsFetcher.load('/api/product-reviews');
      setOpenArgs(args);
    },
    [enabled, hasTagsFailed, tagsFetcher]
  );

  const hidePrompt = useCallback(
    (countsAsDismissal: boolean) => {
      // Read fresh so dismissals from other tabs still count.
      const next = nextDismissal(
        readDismissal(cookieName),
        products.map(({ id }) => id),
        countsAsDismissal
      );
      // Outlives the snooze so the count survives between prompts.
      setCookie(
        cookieName,
        serializeDismissal(next, Date.now()),
        durations.ONE_YEAR
      );
      setDismissal(next);
    },
    [cookieName, products]
  );

  const dismissPrompt = useCallback(() => hidePrompt(true), [hidePrompt]);
  const snoozePrompt = useCallback(() => hidePrompt(false), [hidePrompt]);

  const refreshPrompt = useCallback(() => {
    setProductsVersion((version) => version + 1);
  }, []);

  const value = useMemo(
    () => ({
      dismissPrompt,
      isEnabled: enabled,
      isOpening,
      isReady,
      openLeaveReview,
      promptProducts,
      refreshPrompt,
      snoozePrompt,
    }),
    [
      dismissPrompt,
      enabled,
      isOpening,
      isReady,
      openLeaveReview,
      promptProducts,
      refreshPrompt,
      snoozePrompt,
    ]
  );

  return (
    <LeaveReviewContext.Provider value={value}>
      {children}
      {options && openArgs && (
        <ClientOnly>
          {(isMobile) => (
            <LeaveReviewModal
              key={openArgs.product.id} // Remounts if opened for a different product while already open.
              activityOptions={options.activityOptions}
              feelingOptions={options.feelingOptions}
              isMobile={isMobile}
              moreToReview={openArgs.moreToReview ?? []}
              onClose={(reviewedIds) => {
                openArgs.onClose?.(reviewedIds);
                setOpenArgs(null);
              }}
              product={openArgs.product}
              source={openArgs.source}
            />
          )}
        </ClientOnly>
      )}
    </LeaveReviewContext.Provider>
  );
};
